import React, { Suspense, useEffect, useRef, useState } from 'react';
import Link from '@ff/ui/Link';
import facebook from './icons/icon_facebook.svg';
import instagram from './icons/icon_ins.svg';
import linkedin from './icons/icon_linkedin.svg';
import pinterest from './icons/icon_pinterest.svg';
import tiktok from './icons/icon_tiktok.svg';
import twitter from './icons/icon_twitter.svg';
import wechat from './icons/icon_wechat.svg';
import wecom from './icons/icon_wecom.svg';
import weibo from './icons/icon_weibo.svg';
import youtube from './icons/icon_youtube.svg';
import logo from './icons/logo_padded.svg';
import Box from '@ff/ui/Box';
import { styled } from '@ff/ui';

const QRCode = React.lazy(() => import(/* webpackChunkName: "qrcode" */ 'qrcode.react'));

export interface Props {
  icon?: string;
  url?: string;
}

export default function SocialIcon(props: Props) {
  const { icon, url } = props;

  if (['wecom', 'wechat'].includes(icon)) {
    return (
      <QRIcon {...props} />
    )
  }

  return (
    <Link href={url}>
      <img src={getIcon(icon)} alt={icon} />
    </Link>
  )
}

function QRIcon(props: Props) {
  const { icon, url } = props;

  const ref = useRef();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (e.target !== ref.current) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      window.addEventListener('click', onClickOutside);
    }

    return () => {
      window.removeEventListener('click', onClickOutside);
    };
  }, [isOpen]);

  return (
    <Box position="relative">
      <img
        ref={ref}
        src={getIcon(icon)}
        alt={icon}
        onClick={() => { setIsOpen(open => !open); }}
      />
      {
        isOpen &&
        <>
          <QRWrapper>
            <QRBorder>
              <Mobile>
                <Suspense fallback={<div />}>
                  <QRCode
                    size={50}
                    value={url}
                    // TBD: Embed logo in image
                    // imageSettings={{
                    //   src: logo,
                    //   excavate: true,
                    //   width: 33,
                    //   height: 27
                    // }}
                  />
                </Suspense>
              </Mobile>
              <Desktop>
                <Suspense fallback={<div />}>
                  <QRCode
                    size={112}
                    value={url}
                    // TBD: Embed logo in image
                    // imageSettings={{
                    //   src: logo,
                    //   excavate: true,
                    //   width: 33,
                    //   height: 27
                    // }}
                  />
                </Suspense>
              </Desktop>
            </QRBorder>
          </QRWrapper>
          <Triangle />
        </>
      }
    </Box>
  )
}

const Mobile = styled(Box)(({ theme }) => ({
  [theme.mediaQueries.md]: {
    display: 'none',
  }
}));

const Desktop = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.mediaQueries.md]: {
    display: 'block',
  }
}));

const QRWrapper = styled(Box)(({ theme }) => ({
  position: 'absolute',
  zIndex: '3',
  backgroundColor: 'rgba(217, 217, 217, 0.4)',

  top: '-71px',
  left: 'calc((-64px + 100%) / 2)',
  padding: '3px',

  [theme.mediaQueries.md]: {
    top: '-177px',
    left: 'calc((-144px + 100%) / 2)',
    padding: '6px',
  }
}));

const QRBorder = styled(Box)(({ theme }) => ({
  backgroundColor: 'white',
  // Workaround for mysterious 4px below the QR code
  padding: "4px 4px 0px 4px",

  [theme.mediaQueries.md]: {
    // Workaround for mysterious 4px below the QR code
    padding: "10px 10px 6px 10px",
  }
}));

const Triangle = styled(Box)(({ theme }) => ({
  position: "absolute",
  zIndex: "3",
  width: "0",
  height: "0",

  top: "-7px",
  left: '4px',
  borderLeft: "6px solid transparent",
  borderRight: "6px solid transparent",
  borderTop: "6px solid rgba(217, 217, 217, 0.4)",

  [theme.mediaQueries.md]: {
    top: "-33px",
    left: '-50%',
    borderLeft: "20px solid transparent",
    borderRight: "20px solid transparent",
    borderTop: "20px solid rgba(217, 217, 217, 0.4)",
  }
}));

function getIcon(icon: string) {
  switch (icon) {
    case 'facebook':
      return facebook;
    case 'instagram':
      return instagram;
    case 'linkedin':
      return linkedin;
    case 'pinterest':
      return pinterest;
    case 'tiktok':
      return tiktok;
    case 'twitter':
      return twitter;
    case 'wechat':
      return wechat;
    case 'wecom':
      return wecom;
    case 'weibo':
      return weibo;
    case 'youtube':
      return youtube;
  }
}
